import cloneDeep from "lodash/cloneDeep";
import {
  GET_RESULTS_REQUEST,
  GET_RESULTS_SUCCESS,
  GET_RESULTS_ERROR,
  GET_RAG_RESULT_REQUEST,
  GET_RAG_RESULT_SUCCESS,
  GET_RAG_RESULT_ERROR,
  SET_RAG_VISIBILITY,
  FILTER_RESULTS_REQUEST,
  FILTER_RESULTS_SUCCESS,
  FILTER_RESULTS_ERROR,
  MORE_RESULTS_REQUEST,
  MORE_RESULTS_SUCCESS,
  MORE_RESULTS_ERROR,
  CLEAR_RESULTS_NOTIFICATION,
  CLEAR_SHOULD_SCROLL_TO_TOP,
  CLEAR_FILTER_RESULTS_ERROR,
} from "./action-types";
import {
  cleanQuery,
  updateAddedOrRemovedFilters,
  formatTempFilters,
  updateFiltersForCheckBox,
} from "./helper";

const fetchReset = {
  isFetching: false,
  isFetchingMore: false,
  isFiltering: false,
  hasFetched: false,
};

const fetchResetRag = {
  isFetchingRag: false,
  hasFetchedRag: false,
};

const errorReset = {
  error: {
    hasErrored: false,
  },
  notification: {
    isOpen: false,
  },
};

const shouldScrollToTopReset = { shouldScrollToTop: false };

const searchParamsReset = { searchParams: {} };

export const getShouldScrollToTop = (previousSearchParams, newSearchParams) => {
  const {
    types: prevTypesFilter,
    populations: prevPopulationsFilter,
    specialities: prevSpecialitiesFilter,
    q: prevQueryTerm,
    lang: prevLang,
  } = previousSearchParams;
  const { types, populations, specialities, q, lang } = newSearchParams || {};

  if (
    prevTypesFilter !== types ||
    prevPopulationsFilter !== populations ||
    prevSpecialitiesFilter !== specialities ||
    prevQueryTerm !== q ||
    prevLang !== lang
  ) {
    return true;
  }
  return false;
};

export const getIsNewSearch = (previousSearchParams, newSearchParams) => {
  const { q: prevQueryTerm, lang: prevLang } = previousSearchParams;
  const { q, lang } = newSearchParams || {};

  return prevQueryTerm !== q || prevLang !== lang;
};

const getFilterControls = (state, payload) => {
  const previousFilters = state.filterControls.selectedFilters;
  const selectedFilters = payload.viewModel.tabs.reduce(
    (previousValue, currentValue) => {
      previousValue[currentValue.id] = currentValue.selectedFilters;
      return previousValue;
    },
    {}
  );
  return {
    selectedFilters,
    previousFilters,
  };
};

const modifiedTabs = (state, payload, tabName) => {
  const tabs = payload.viewModel.tabs.map((tab) => {
    if (tab.id === tabName) {
      return tab;
    }
    const currentTabs = state.tabs.find((stateTab) => stateTab.id === tab.id);
    const updatedTabs = {
      ...tab,
      selectedFilters: currentTabs?.selectedFilters,
    };
    return updatedTabs;
  });
  return tabs;
};

export const initialState = {
  ...fetchReset,
  ...fetchResetRag,
  header: {},
  tabs: [],
  tabContents: {
    main: {},
  },
  filterControls: {
    selectedFilters: {},
    previousFilters: {},
  },
  ...searchParamsReset,
  ...shouldScrollToTopReset,
  ...errorReset,
  resultsUsurpers: null,
  rag: {},
  ragEnabled: false,
  ragShow: false,
  hasRagError: false,
  placardTypes: [],
};

export function reducer(state, action) {
  switch (action.type) {
    // Do not clear the previous search until we get the response
    case GET_RESULTS_REQUEST: {
      return {
        ...state,
        ...fetchReset,
        ...errorReset,
        isFetching: true,
      };
    }
    case GET_RESULTS_SUCCESS: {
      let notification = initialState.notification;
      if (action.payload.viewModel.translationWarning?.message) {
        notification = {
          type: "warning",
          message: action.payload.viewModel.translationWarning.message,
          isOpen: true,
        };
      }
      const tabName = action.payload.tab;
      let tabContents = {
        ...state.tabContents,
        ...action.payload.viewModel.tabContents,
      };
      tabContents[tabName] = {
        ...tabContents[tabName],
        routeData: {
          ...tabContents[tabName]?.routeData,
          initialQuery: action.payload.initialQuery,
        },
      };
      const resultsUsurpersArray = tabContents[
        tabName
      ]?.ancillaryResults?.filter(
        (q) => q.type === "mainResultUsurperPlacards"
      );

      let resultsUsurpers = resultsUsurpersArray.map((unclonedUsurper) => {
        let clonedUsurper = cloneDeep(unclonedUsurper?.results?.[0] || null);
        if (
          Array.isArray(clonedUsurper?.body?.group) &&
          clonedUsurper.body.group.length > 0
        ) {
          const desktopColumns = [{ group: [] }, { group: [] }, { group: [] }];
          clonedUsurper.body.group.forEach((group, index) => {
            desktopColumns[index % 3].group.push(group);
          });
          clonedUsurper.desktopColumns = desktopColumns;
          const tabletColumns = [{ group: [] }, { group: [] }];
          clonedUsurper.body.group.forEach((group, index) => {
            tabletColumns[index % 2].group.push(group);
          });
          clonedUsurper.tabletColumns = tabletColumns;
          clonedUsurper.telemetryData = tabContents[tabName]?.telemetryData;
        }
        return clonedUsurper;
      });

      if (resultsUsurpers.length === 0) {
        resultsUsurpers = null;
      }

      if (!getIsNewSearch(state.searchParams, action.payload.params)) {
        resultsUsurpers = resultsUsurpers || state.resultsUsurpers;
      }
      const placardTypes =
        tabContents[tabName].ancillaryResults
          ?.find(
            (result) =>
              result.type === "placards" ||
              result.type === "mainResultUsurperPlacards"
          )
          ?.results.map((placard) => placard.type) || [];
      return {
        filterControls: getFilterControls(state, action.payload),
        ...fetchReset,
        hasFetched: true,
        hasFetchedRag:
          state.header?.title !== action.payload.initialQuery.q
            ? false
            : state.hasFetchedRag,
        isFetchingRag: state.isFetchingRag,
        ...errorReset,
        ...action.payload.viewModel,
        tabContents,
        tabs: modifiedTabs(state, action.payload, tabName),
        notification,
        searchParams: action.payload.params || {},
        shouldScrollToTop: getShouldScrollToTop(
          state.searchParams,
          action.payload.params
        ),
        resultsUsurpers,
        ragEnabled: action.payload.viewModel.ragEnabled,
        rag: state.rag,
        ragShow: state.ragShow,
        hasRagError: state.hasRagError,
        placardTypes,
      };
    }
    case GET_RESULTS_ERROR: {
      let tabContents = {};
      const tabName = action.payload.tab;
      tabContents[tabName] = {
        routeData: {
          initialQuery: action.payload.initialQuery,
        },
      };
      return {
        ...initialState,
        ...fetchReset,
        ...fetchResetRag,
        ...errorReset,
        ...searchParamsReset,
        ...shouldScrollToTopReset,
        ...action.payload.viewModel,
        tabContents,
        error: action.error,
      };
    }
    case GET_RAG_RESULT_REQUEST: {
      return {
        ...state,
        isFetchingRag: true,
        hasFetchedRag: false,
        hasRagError: false,
      };
    }
    case GET_RAG_RESULT_SUCCESS: {
      return {
        ...state,
        isFetchingRag: false,
        hasFetchedRag: true,
        hasRagError: false,
        rag: { ...action.payload.viewModel },
      };
    }
    case GET_RAG_RESULT_ERROR: {
      return {
        ...state,
        isFetchingRag: false,
        hasFetchedRag: true,
        hasRagError: true,
        rag: initialState.rag,
      };
    }
    case SET_RAG_VISIBILITY: {
      return {
        ...state,
        ragShow: action.payload.show,
      };
    }
    case MORE_RESULTS_REQUEST: {
      return {
        ...state,
        ...fetchReset,
        ...errorReset,
        hasFetched: true,
        isFetchingMore: true,
      };
    }
    case MORE_RESULTS_SUCCESS: {
      const baseState = {
        ...state,
        ...fetchReset,
        ...errorReset,
        hasFetched: true,
      };
      const moreOfType = action.payload.tab;
      baseState.tabContents[moreOfType].primaryResults = [
        ...state.tabContents[moreOfType].primaryResults,
        ...action.payload.viewModel.tabContents[moreOfType].primaryResults,
      ];
      baseState.tabContents[moreOfType].loadMore =
        action.payload.viewModel.tabContents[moreOfType].loadMore;
      baseState.tabContents[moreOfType].routeData = {
        ...baseState.tabContents[moreOfType].routeData,
        ...action.payload.viewModel.tabContents[moreOfType].routeData,
      };

      const articlesReturned = [
        ...baseState.tabContents[moreOfType].telemetryData.articlesReturned,
        ...action.payload.viewModel.tabContents[moreOfType].telemetryData
          .articlesReturned,
      ];

      baseState.tabContents[moreOfType].telemetryData = {
        ...action.payload.viewModel.tabContents[moreOfType].telemetryData,
        articlesReturned,
      };

      return baseState;
    }
    case MORE_RESULTS_ERROR: {
      return {
        ...state,
        ...fetchReset,
        ...fetchResetRag,
        ...errorReset,
        hasFetched: true,
        notification: {
          type: "error",
          message: action.error.message,
          isOpen: true,
        },
      };
    }
    case FILTER_RESULTS_REQUEST: {
      if (action.payload.query !== action.payload.prevQueryParams) {
        const filterGroups = cleanQuery(action.payload.query);
        const prevFilterGroups = cleanQuery(action.payload.prevQueryParams);

        const diff = updateAddedOrRemovedFilters(
          filterGroups,
          prevFilterGroups
        );

        updateFiltersForCheckBox(state, diff, action.payload.tab);

        const tempTab = state.tabs.find((tab) => tab.id === action.payload.tab);

        const finalTempFiltersForChips = formatTempFilters(
          filterGroups,
          tempTab
        );

        tempTab.selectedFilters = finalTempFiltersForChips;
      }

      return {
        ...state,
        ...fetchReset,
        ...state.hasFetched,
        ...errorReset,
        isFiltering: true,
      };
    }
    case FILTER_RESULTS_SUCCESS: {
      let notification = initialState.notification;
      if (action.payload.viewModel.translationWarning?.message) {
        notification = {
          type: "warning",
          message: action.payload.viewModel.translationWarning.message,
          isOpen: true,
        };
      }
      const tabName = action.payload.tab;
      let tabContents = {
        ...state.tabContents,
        ...action.payload.viewModel.tabContents,
      };

      tabContents[tabName] = {
        ...tabContents[tabName],
        routeData: {
          ...tabContents[tabName]?.routeData,
          initialQuery: action.payload.initialQuery,
        },
      };

      return {
        ...state,
        ...fetchReset,
        hasFetched: true,
        ...errorReset,
        ...action.payload.viewModel,
        tabContents,
        tabs: modifiedTabs(state, action.payload, tabName),
        notification,
        filterControls: getFilterControls(state, action.payload),
        searchParams: action.payload.params || {},
      };
    }
    case FILTER_RESULTS_ERROR: {
      return {
        ...state,
        ...fetchReset,
        hasFetched: true,
        filterError: true,
        ...errorReset,
        notification: {
          type: "error",
          message: action.error.message,
          isOpen: true,
        },
      };
    }
    case CLEAR_FILTER_RESULTS_ERROR: {
      return {
        ...state,
        filterError: false,
      };
    }
    case CLEAR_RESULTS_NOTIFICATION: {
      return {
        ...state,
        notification: {
          ...state.notification,
          isOpen: false,
        },
      };
    }
    case CLEAR_SHOULD_SCROLL_TO_TOP: {
      return {
        ...state,
        ...shouldScrollToTopReset,
      };
    }
    default:
      return state;
  }
}
